import type { CommitmentsData, Locale, NationalProgram, SectorDefinition, Theme } from '../types';
import { chrome, formatDataDate } from '../i18n';
import { PlatformBar } from '../components/PlatformBar';
import { Breadcrumb } from '../components/Breadcrumb';
import { icon } from '../components/Icon';
import { attainment, ragFor } from '../core/targets';
import { programStatus, programPill } from '../core/programs';

interface Props {
  sectors: SectorDefinition[];
  commitments: CommitmentsData;
  program: NationalProgram;
  locale: Locale;
  theme: Theme;
}

/** Tier 2 — program detail: milestones, contributing KPIs, sectors involved. */
export function ProgramView({ sectors, commitments, program, locale, theme }: Props): string {
  const c = chrome(locale);
  const pc = program.content[locale];
  const status = programStatus(program);
  const pill = programPill(status);
  const today = new Date().toISOString().slice(0, 10);

  const milestones = pc.milestones
    .map((m) => {
      const overdue = !m.done && m.due < today;
      const state = m.done ? 'done' : overdue ? 'overdue' : 'pending';
      return `
      <div class="milestone ${state}">
        <span class="ms-ic">${icon(m.done ? 'check-circle-2' : 'circle')}</span>
        <div class="ms-body"><div class="ms-label">${m.label}</div>
          <div class="ms-due">${icon('calendar', 'icon icon-xs')} ${formatDataDate(locale, m.due)}${overdue ? ` · ${c.overdue}` : ''}</div>
        </div>
      </div>`;
    })
    .join('');

  const kpiCards = (program.kpis ?? [])
    .map((ref) => {
      const sector = sectors.find((s) => s.id === ref.sector);
      const k = sector?.content[locale].kpis.find((x) => x.id === ref.kpi);
      if (!sector || !k) return '';
      const att = attainment(k);
      const rag = att !== undefined ? ragFor(att) : undefined;
      return `
      <a class="card kpi mini" style="--accent:${sector.accent}" href="#/${sector.id}">
        <div class="label"><span class="ic" style="background:${sector.accent}22;color:${sector.accent}">${icon(k.icon, 'icon icon-kpi')}</span>${k.label}</div>
        <div><span class="val">${k.value}</span> <span class="unit">${k.unit}</span></div>
        ${rag ? `<div class="target ${rag}"><div class="t-bar"><span style="width:${Math.min(att!, 100)}%"></span></div><span class="t-label">${c.targetLabel}: ${k.target?.label ?? ''}</span></div>` : ''}
      </a>`;
    })
    .join('');

  const sectorChips = program.sectors
    .map((id) => {
      const s = sectors.find((x) => x.id === id);
      if (!s) return '';
      return `<a class="tag sector-tag" style="color:${s.accent};background:${s.accent}18" href="#/${s.id}">${icon(s.icon, 'icon icon-xs')} ${s.content[locale].navLabel}</a>`;
    })
    .join('');

  return `
    ${PlatformBar(sectors, 'commitments', locale, theme, true, formatDataDate(locale, commitments.updatedAt))}
    ${Breadcrumb([
      { label: c.navNational, href: 'national', icon: 'gauge' },
      { label: c.navCommitments, href: 'commitments', icon: 'flag' },
      { label: pc.name, icon: program.icon },
    ])}
    <section class="program-head" style="--accent:${program.accent}">
      <div class="ph-left">
        <div class="ph-title"><span class="ic" style="background:${program.accent}22;color:${program.accent}">${icon(program.icon, 'icon icon-kpi')}</span>
          <div><h1>${pc.name}</h1><p>${pc.subtitle}</p></div>
        </div>
        <div class="ph-meta">
          <span>${icon('user', 'icon icon-xs')} ${pc.owner}</span>
          <span>${icon('flag', 'icon icon-xs')} ${pc.pillar}</span>
          <span>${icon('calendar', 'icon icon-xs')} ${formatDataDate(locale, program.due)}</span>
        </div>
        <div class="program-stats big">
          <span class="pill ${pill}">${c.progStatus[status]}</span>
          <div class="progress-track wide"><span class="planned-marker" style="inset-inline-start:${program.planned}%"></span><span class="progress-fill" style="width:${program.progress}%"></span></div>
          <span><b>${program.progress}%</b> ${c.delivery} · ${c.planned} ${program.planned}%</span>
        </div>
      </div>
    </section>

    <section class="grid cols" style="margin-top:16px">
      <div class="panel span6">
        <h3 class="panel-title">${icon('flag')} <span>${c.milestonesLabel}</span></h3>
        <div class="milestones">${milestones}</div>
      </div>
      <div class="panel span6">
        <h3 class="panel-title">${icon('target')} <span>${c.contributingKpis}</span></h3>
        <div class="grid kpis mini-grid">${kpiCards}</div>
        <div class="touched"><span class="muted">${c.sectorsTouched}:</span> ${sectorChips}</div>
      </div>
    </section>
    <div id="modal-root"></div>`;
}
